import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { cn } from './cn';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Prevent closing via backdrop/Escape/swipe (e.g. while submitting). */
  dismissible?: boolean;
  className?: string;
  /**
   * `first` (default): the browser focuses the first field/button, so a form's input is ready to type in.
   * `sheet`: the sheet itself takes focus, so the on-screen keyboard stays down until the person taps a field.
   */
  initialFocus?: 'first' | 'sheet';
}

/** Drag further than this (px), or flick faster than FLICK (px/ms), to dismiss. */
const DISMISS_DISTANCE = 110;
const FLICK = 0.6;

/**
 * Accessible modal built on the native <dialog>: focus trap, Escape handling and
 * inert background come for free. Bottom sheet on phones (swipe the handle down to close),
 * centred dialog on desktop.
 */
export function Sheet({ open, onClose, title, children, dismissible = true, className, initialFocus = 'first' }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startY: number; startT: number; dy: number } | null>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  const setOffset = (dy: number, animate: boolean) => {
    const el = panel.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none';
    el.style.transform = dy > 0 ? `translateY(${dy}px)` : '';
  };

  const onDragStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dismissible) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startY: e.clientY, startT: e.timeStamp, dy: 0 };
  };
  const onDragMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    d.dy = Math.max(0, e.clientY - d.startY);
    setOffset(d.dy, false);
  };
  const onDragEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const speed = d.dy / Math.max(1, e.timeStamp - d.startT);
    if (d.dy > DISMISS_DISTANCE || (d.dy > 24 && speed > FLICK)) {
      setOffset(window.innerHeight, true);
      window.setTimeout(() => {
        setOffset(0, false);
        onClose();
      }, 180);
    } else {
      setOffset(0, true);
    }
  };

  return (
    <dialog
      ref={ref}
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(e) => {
        if (dismissible && e.target === ref.current) onClose();
      }}
      className={cn(
        'm-0 mt-auto w-full max-w-none bg-transparent p-0 text-ink backdrop:animate-backdrop-in backdrop:backdrop-blur-[3px]',
        'md:m-auto md:max-w-lg',
      )}
    >
      {open && (
        <div
          ref={panel}
          tabIndex={initialFocus === 'sheet' ? -1 : undefined}
          className={cn(
            initialFocus === 'sheet' && 'outline-none',
            'max-h-[90dvh] animate-sheet-in overflow-y-auto overscroll-contain rounded-t-[28px] bg-paper px-5 pb-[max(2rem,calc(env(safe-area-inset-bottom)+1rem))] pt-0 shadow-floating',
            'md:rounded-[28px] md:px-5 md:pb-6 md:pt-5',
            className,
          )}
        >
          {/* Grab area: a generous touch target for swiping the sheet away (phones only). */}
          <div
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
            className="-mx-5 mb-2 flex h-9 cursor-grab touch-none items-center justify-center md:hidden"
            aria-hidden
          >
            <span className="h-1.5 w-10 rounded-full bg-line" />
          </div>
          {title && <h2 className="mb-4 text-lg font-bold">{title}</h2>}
          {children}
        </div>
      )}
    </dialog>
  );
}
